import { ReadonlySignal, Signal, effect } from "@preact/signals-core";
import { Howl } from "howler";
import { PRELOAD_SOUNDS } from "./Globals";

import { createLogger, type Logger } from "../shared/Logger";
const logger = createLogger("SoundManager") as Logger;

const StorageKey = "Options";

class SoundOptions {
  private _musicEnabled: boolean = false;
  private _soundEnabled: boolean = true;
  private _musicVolume: number = 0.5;
  private _soundVolume: number = 0.85;

  public get musicEnabled(): boolean {
    return this._musicEnabled;
  }
  public set musicEnabled(value: boolean) {
    if (value != this._musicEnabled) {
      this._musicEnabled = value;
    }
  }

  public get soundEnabled(): boolean {
    return this._soundEnabled;
  }
  public set soundEnabled(value: boolean) {
    if (value != this._soundEnabled) {
      this._soundEnabled = value;
    }
  }

  public get musicVolume(): number {
    return this._musicVolume;
  }
  public set musicVolume(value: number) {
    if (value != this._musicVolume) {
      this._musicVolume = value;
    }
  }

  public get soundVolume(): number {
    return this._soundVolume;
  }
  public set soundVolume(value: number) {
    if (value != this._soundVolume) {
      this._soundVolume = value;
    }
  }

  public toJSON() {
    let { soundVolume, musicVolume, musicEnabled, soundEnabled } = this;
    return { soundVolume, musicVolume, musicEnabled, soundEnabled };
  }

  public load() {
    const optJson = localStorage.getItem(StorageKey) || JSON.stringify(this);
    const opt = JSON.parse(optJson) as SoundOptions;
    this.musicEnabled = opt.musicEnabled ?? true;
    this.soundEnabled = opt.soundEnabled ?? true;
    this.musicVolume = opt.musicVolume ?? 1;
    this.soundVolume = opt.soundVolume ?? 1;
  }
  public save() {
    const str = JSON.stringify(this);
    localStorage.setItem(StorageKey, str);
  }
}

/**
 * Ambient music type, applies only while in flight mode.
 */
export enum AmbientType {
  None,
  Action,
  Danger,
}


export class SoundManager {
  private readonly allSounds: Map<string, Howl> = new Map();
  private optionsChangedSignal: ReadonlySignal<SoundOptions>;

  /**
   * Background music are music tracks that are played in the background.
   */
  private backMusic: Howl | undefined;

  /**
   * Ambient music are music tracks that are played to create ambient atmosphere, they are dynamically changed based on the player situation.
   */
  private ambientMusic: Howl | undefined;
  private ambientType: AmbientType = AmbientType.None;

  constructor() {
    PRELOAD_SOUNDS.forEach((s) => {
      const startIdx = s.lastIndexOf("/") + 1;
      const endIdx = s.lastIndexOf(".ogg");
      const name = s.substring(startIdx, endIdx);
      this.allSounds.set(
        name,
        new Howl({
          src: s,
          html5: false,
        }),
      );
    });

    const options = new SoundOptions();
    options.load();
    this.optionsChangedSignal = new Signal(options);

    effect(() => {
      const opt = this.optionsChangedSignal.value;
      this.handleSoundOptionsChange(opt);
    });
  }

  /**
   * Returns the sound options.
   */
  public get soundOptions() {
    return this.optionsChangedSignal;
  }

  public playSound(soundName: string, loop: boolean = false): number {
    const howl = this.allSounds.get(soundName);
    if (!howl) {
      logger.warn(`sound '${soundName}' not found!`);
      return -1;
    }
    const sndId = howl.play();
    howl.volume(this.optionsChangedSignal.value.soundVolume, sndId);
    if (loop) {
      howl.loop(true, sndId);
    }
    if (!this.optionsChangedSignal.value.soundEnabled) {
      howl.mute(true, sndId);
    }
    return sndId;
  }

  public stopSound(soundName: string, id?: number) {
    const howl = this.allSounds.get(soundName);
    if (!howl) {
      logger.warn(`sound '${soundName}' not found!`);
      return -1;
    }
    howl.stop(id);
  }

  public playMusic(soundName: string, fadeOutTime = 500, fadeInTime = 750) {
    logger.log("play music", soundName);
    const howl = this.allSounds.get(soundName);
    if (!howl) {
      logger.warn(`sound '${soundName}' not found!`);
      return -1;
    }
    if (howl.playing()) {
      howl.stop();
    }

    const sndId = howl.play();
    howl.loop(true, sndId);
    howl.volume(this.optionsChangedSignal.value.musicVolume, sndId);
    howl.fade(
      0,
      this.optionsChangedSignal.value.musicVolume,
      fadeInTime,
      sndId,
    );
    if (!this.optionsChangedSignal.value.musicEnabled) {
      howl.mute(true, sndId);
    }

    if (this.backMusic && this.backMusic != howl && this.backMusic.playing()) {
      const stopping = this.backMusic;
      stopping.fade(1, 0, fadeOutTime);
      stopping.once("fade", stopping.stop);
    }
    this.backMusic = howl;
  }

  public stopMusic() {
    if (this.backMusic) {
      this.backMusic.stop();
      this.backMusic = undefined;
    }
  }

  private handleSoundOptionsChange = (opt: SoundOptions) => {
    if (this.backMusic) {
      this.backMusic.mute(!opt.musicEnabled);
    }
    if (this.backMusic) {
      this.backMusic.volume(opt.musicVolume);
    }
    if (this.ambientMusic && this.ambientMusic.playing()) {
      this.ambientMusic.volume(opt.musicVolume);
      this.setBackMusicVolumeForAmbient();
    }
    this.allSounds.forEach((howl) => {
      if (howl !== this.backMusic) {
        howl.mute(!opt.soundEnabled);
      }
    });
    this.allSounds.forEach((howl) => {
      if (howl !== this.backMusic && howl !== this.ambientMusic) {
        howl.volume(opt.soundVolume);
      }
    });
  };

  /**
   * Sets the volume to 100% if no ambient is set or 50% for ambient music.
   */
  private setBackMusicVolumeForAmbient() {
    if (this.backMusic && this.backMusic.playing()) {
      this.backMusic.volume(
        this.ambientType == AmbientType.None
          ? this.optionsChangedSignal.value.musicVolume
          : this.optionsChangedSignal.value.musicVolume / 2,
      );
    }
  }
}

export const soundMan = new SoundManager();
